"use client"
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { toLocalISOString } from '@/lib/formatHour';
import { useDateRange } from '@/components/date/DateRangeContext';
import { useCityFromUrl, generateCityUrl } from '@/lib/cityUtils';
import { useCityContext } from '@/lib/CityContext';
import CitySelectionModal from '../ui/CitySelectionModal';
import { useAnalytics } from '@/hooks/useAnalytics';
import { track, setUserProperties } from '@/lib/tracking';

interface SearchRedirectBtnProps {
  children: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
  onClick?: () => void;
  textColor?: string;
  analyticsName?: string; // Custom analytics name for tracking
  buttonText?: string; // Button text to determine default filters
}

const SearchRedirectBtn: React.FC<SearchRedirectBtnProps> = ({
  children,
  icon,
  className = '',
  onClick,
  textColor = 'white',
  analyticsName = 'search_redirect_button', // Default analytics name
  buttonText,
  ...props
}) => {
  const router = useRouter();
  const [showPopup, setShowPopup] = useState(false);
  const { range } = useDateRange();
  const { isLoading } = useCityContext();
  const { trackButtonClick } = useAnalytics();

  // Get city from URL using centralized context
  const currentCity = useCityFromUrl();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // Force prevent default and stop propagation immediately
    e.preventDefault();
    e.stopPropagation();
    
  
    
    trackButtonClick(analyticsName, 'Search Navigation', {
      city_name: currentCity.name || 'none',
      city_id: currentCity._id || 'none',
      button_text: typeof children === 'string' ? children : 'Search Button',
      has_city_selected: !!(currentCity.name && currentCity._id),
      from_date: toLocalISOString(range.startDate),
      to_date: toLocalISOString(range.endDate)
    });

    const fromDate = toLocalISOString(range.startDate);
    const toDate = toLocalISOString(range.endDate);
    const durationDays = Math.max(1, Math.round(
      (range.endDate.getTime() - range.startDate.getTime()) / (1000 * 60 * 60 * 24)
    ));
    track('search_initiated', {
      city: currentCity.name || '',
      from_date: fromDate,
      to_date: toDate,
      duration_days: durationDays,
    });
    setUserProperties({ preferred_city: currentCity.name || '' });
    
    // More strict validation - check for empty strings, null, undefined, and whitespace
    const hasValidCityName = currentCity.name && currentCity.name.trim() !== '';
    const hasValidCityId = currentCity._id && currentCity._id.trim() !== '';
    
    // Always require city selection - no navigation without city
    if (!hasValidCityName || !hasValidCityId) {
      setShowPopup(true);
      return;
    }
    
    // Only proceed with navigation and onClick if city is selected
    if (onClick) {
      onClick();
    }
    
    // Always use range from useDateRangePicker if available
    const from = toLocalISOString(range.startDate);
    const to = toLocalISOString(range.endDate);
    const params = new URLSearchParams();
    params.set('from', from);
    params.set('to', to);
    
    // Add buttonText as URL parameter if provided
    if (buttonText) {
      params.set('buttonText', buttonText);
    }
    
    // Navigate to city-specific search
    const url = generateCityUrl(currentCity.name, 'bike-rentals/search', params);
    router.push(url);
  };

  // Show loading state while cities are being fetched
  if (isLoading) {
    return (
      <button
        type="button"
        disabled
        className={`flex items-center justify-center gap-2 bg-gray-400 cursor-not-allowed text-white text-[12px] lg:text-[16px] font-semibold py-2 px-4 lg:px-8 rounded-4xl transition whitespace-nowrap ${className}`}
        {...props}
      >
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-white rounded-full animate-bounce"></div>
          <span>Loading...</span>
        </div>
      </button>
    );
  }

  return (
    <>
      {/* City Selection Modal */}
      <CitySelectionModal 
        isOpen={showPopup} 
        onClose={() => setShowPopup(false)} 
      />

      
      <button
        type="button"
        onClick={handleClick}
        className={`flex items-center justify-center gap-2 bg-[#F05A24] ${!currentCity.name ? 'cursor-not-allowed' : 'hover:bg-[#F05A24]/90 cursor-pointer'} text-${textColor} text-[12px] lg:text-[16px] font-semibold py-2 px-4 lg:px-8 rounded-4xl transition whitespace-nowrap ${className}`}
        title={!currentCity.name ? 'Please select a city first.' : 'Search for bike rentals'}
        aria-disabled={!currentCity.name}
        {...props}
      >
        {icon && <>{icon}</>}
        {children}
      </button>
    </>
  );
};

export default SearchRedirectBtn;